import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  DollarSign, 
  Clock, 
  AlertCircle,
  CheckCircle,
  PauseCircle,
  Store,
  Calendar
} from 'lucide-react';

interface StatsCardProps {
  title: string;
  value: number | string;
  description?: string;
  trend?: number;
  icon: 'shops' | 'active' | 'trial' | 'suspended' | 'revenue' | 'expiring' | 'overdue' | 'total';
  className?: string;
  loading?: boolean;
}

const icons = {
  shops: Store,
  active: CheckCircle,
  trial: Clock,
  suspended: PauseCircle,
  revenue: DollarSign,
  expiring: AlertCircle,
  overdue: AlertCircle,
  total: Users,
};

const colors = {
  shops: 'bg-blue-50 text-blue-700',
  active: 'bg-green-50 text-green-700',
  trial: 'bg-yellow-50 text-yellow-700',
  suspended: 'bg-red-50 text-red-700',
  revenue: 'bg-purple-50 text-purple-700',
  expiring: 'bg-orange-50 text-orange-700',
  overdue: 'bg-red-50 text-red-700',
  total: 'bg-indigo-50 text-indigo-700',
};

export function StatsCard({ title, value, description, trend, icon, className = '', loading = false }: StatsCardProps) {
  const Icon = icons[icon];
  const colorClass = colors[icon];

  if (loading) {
    return (
      <Card className={`p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-full"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className={`p-6 ${className}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
          
          {description && (
            <p className="text-sm text-gray-500 mt-1">{description}</p>
          )}
          
          {trend !== undefined && (
            <div className="flex items-center mt-2">
              {trend > 0 ? (
                <>
                  <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">{trend}%</span>
                </>
              ) : (
                <>
                  <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
                  <span className="text-sm text-red-600">{Math.abs(trend)}%</span>
                </>
              )}
              <span className="text-sm text-gray-500 ml-1">vs mes anterior</span>
            </div>
          )}
        </div>
        
        <div className={`p-3 rounded-full ${colorClass}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </Card>
  );
}